import { useAtom } from "jotai";
import type React from "react";
import { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FitText from "~/components/reception/FitText";
import { useNavigate } from "react-router-dom";
import { languageAtom } from "~/Atom";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import ReceptionCharacterCanvas from "~/components/reception/ReceptionCharacterCanvas";
import { receptionBowEndExpression } from "~/reception/motion/receptionBowMotion";
import { getReceptionCommonWord } from "~/utils/receptionDB";
import type { ReceptionCommonWordEntity } from "~/utils/receptionDB.types";
import { getReceptionCommonWordsByLanguage, useInactivityTimeout } from "~/utils/receptionUtils";
import "~/css/reception/CompleteReceptionPage.css";
import ReceptionHeader from "~/components/ReceptionHeader";
import { ROUTE } from "~/constants/routes";

/**
 * キャラクター Canvas に渡す style。
 * 参照安定化のためモジュールスコープに置き、レンダー毎の再生成を避ける
 * （ReceptionStandbyScreen.tsx の canvasStyle と同様の運用）。
 */
const characterCanvasStyle: CSSProperties = {
  aspectRatio: "1 / 1",
  height: "100%",
};

/**
 * 受付完了画面。
 * 完了メッセージの表示と音声再生、および無操作時の自動TOP遷移を行う。
 */
const CompleteReceptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [language] = useAtom(languageAtom);
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
    endExpression: receptionBowEndExpression(),
  });

  const [receptionCommonWord, setReceptionCommonWord] = useState<
    ReceptionCommonWordEntity | undefined
  >(undefined);

  const completeMessage = useMemo((): string => {
    return (
      getReceptionCommonWordsByLanguage(receptionCommonWord, language)?.receptionCompleteMessage ??
      ""
    );
  }, [language, receptionCommonWord]);

  useInactivityTimeout(navigate);

  useEffect(() => {
    const resolve = async () => {
      try {
        const currentWord = await getReceptionCommonWord();
        setReceptionCommonWord(currentWord);
      } catch (err) {
        console.error("Failed to load reception common word:", err);
        setReceptionCommonWord(undefined);
      }
    };
    void resolve();
  }, []);

  useEffect(() => {
    void playWithCooldown(completeMessage).catch((error) => {
      console.error("completeReception playVoice failed:", error);
    });
  }, [playWithCooldown]);

  const handleBackToTop = useCallback(() => {
    navigate(ROUTE.RECEPTION.LOADING, { state: { skipStartupModal: true } });
  }, [navigate]);

  return (
    <div className="reception-complete-page">
      <ReceptionHeader />
      <div className="reception-complete__content">
        <div className="reception-complete__title-group">
          <h2 className="screen__title">
            <FitText>{completeMessage}</FitText>
          </h2>
        </div>
        <div className="reception-complete__character-panel">
          <ReceptionCharacterCanvas canvasStyle={characterCanvasStyle} idleSwitchProbability={1} />
        </div>
        <button type="button" className="text-btn gradient-primary-btn" onClick={handleBackToTop}>
          <span>{t("completeReception.backToTop")}</span>
        </button>
      </div>
    </div>
  );
};

export default CompleteReceptionPage;
